import Divider from '@narvar/nth-bit-divider';
import { MediumHeader } from '@narvar/nth-bit-header';
import { RetailerTheme } from '@narvar/nth-hook-loma';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import dayjs from 'dayjs';
import React, { CSSProperties, FC, useEffect, useState } from 'react';
import 'react-day-picker/dist/style.css';
import {
  StyledCalendarContainer,
  StyledDayPicker,
  StyledNoTimeSelectedContainer,
} from '../styled';
import { getAppointmentTimesDto, ITimesList } from '../types';
import CaptionLabel from './CaptionLabel';
import TimeSelector from './TimeSelector';
import { useTranslator } from '../utils/translator';

interface IProps {
  dateTimes?: getAppointmentTimesDto;
  onDaySelect: (selected: Date | undefined) => void;
  onTimeSelect?: (selected: string) => void;
  localeLanguage?: string;
}

const getDate = (date: string): Date => {
  const [year, month, day] = date.split('-').map((d) => +d);
  return new Date(year, month - 1, day);
};

const formatDate = (year: number, month: number, day: number) => {
  const formattedMonth = month < 10 ? `0${month}` : month.toString();
  const formattedDay = day < 10 ? `0${day}` : day.toString();
  return [year.toString(), formattedMonth, formattedDay].join('-');
};

const getNextDate = (date: string): string => {
  const [year, month, day] = date.split('-').map((d) => +d);
  const nextDate = new Date(year, month - 1, day + 1);
  return formatDate(
    nextDate.getFullYear(),
    nextDate.getMonth() + 1,
    nextDate.getDate(),
  );
};

const getDatesInRange = (from: string, to: string) => {
  const range: string[] = [];
  let current = from;

  while (current <= to) {
    range.push(current);
    current = getNextDate(current);
  }

  return range;
};

const CalendarPanel: FC<IProps> = (props) => {
  const { dateTimes, onDaySelect, onTimeSelect, localeLanguage } = props;
  const { t, tc } = useTranslator();

  const scheduleDeliveryFormHeader = t('Select a Date & Time');
  const scheduleDeliveryNoDateSelected = t('No date selected');
  const scheduleDeliveryInstructionsContentForCarrier = tc(
    'scheduleDeliveryInstructionsContentForCarrier',
  );

  const [selectedDay, setSelectedDay] = useState<Date>();
  const [fromDate, setFromDate] = useState<Date>();
  const [toDate, setToDate] = useState<Date>();
  const [disabledDates, setDisabledDates] = useState<Date[]>([]);

  const [showNoDateSelected, setShowNoDateSelected] = useState<boolean>(true);
  const [showTimes, setShowTimes] = useState<boolean>(false);
  const [showNoTimes, setShowNoTimes] = useState<boolean>(false);

  const [times, setTimes] = useState<ITimesList[]>([]);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [availableTimes, setAvailableTimes] = useState<{
    date: string;
    times: string[];
  } | null>(null);

  useEffect(() => {
    if (!dateTimes?.length) return;

    const availableDates = dateTimes.map((dt) => dt.date).sort();
    const from = availableDates[0];
    const to = availableDates[availableDates.length - 1];

    const range = getDatesInRange(from, to);
    const disabled = range
      .filter((d) => !availableDates.includes(d))
      .map((d) => getDate(d));

    const now = new Date();
    const firstDate = getDate(from);

    setFromDate(now > firstDate ? now : firstDate);
    setToDate(getDate(to));
    setDisabledDates(disabled);
  }, [dateTimes]);

  useEffect(() => {
    onDaySelect(selectedDay);
  }, [selectedDay, onDaySelect]);

  useEffect(() => {
    setShowTimes(false);

    if (!selectedDay) {
      setShowNoTimes(false);
      setShowNoDateSelected(true);
      return;
    }

    setShowNoDateSelected(false);

    // When implementing time picker, this block will check for available
    // times on chosen date and show the appropriate time pick panel
    if (dateTimes) {
      const userSelectedDate = dayjs(selectedDay).format('YYYY-MM-DD');

      const matchingDateTime = dateTimes.find(
        (dateTime) => dateTime.date === userSelectedDate,
      );

      setAvailableTimes(matchingDateTime ? { ...matchingDateTime } : null);
    }
  }, [selectedDay]);

  useEffect(() => {
    if (selectedTime) {
      setShowNoTimes(true);
      setShowTimes(false);
    }
  }, [selectedTime]);

  useEffect(() => {
    if (availableTimes) {
      const timesList: ITimesList[] = availableTimes.times.map((time) => ({
        active: true,
        buttonText: time,
        value: time,
        anteMeridiem: true,
      }));
      setTimes(timesList);

      if (timesList.length > 0) {
        setShowTimes(true);
        setShowNoTimes(false);
      } else {
        setShowNoTimes(true);
        setShowTimes(false);
      }
    }
  }, [availableTimes]);

  const { bodytextDefaultNone: bodyText, iconsPrimaryNone: iconsPrimary } =
    useTheme() as {
      [key in keyof RetailerTheme]: CSSProperties;
    };

  const handleTimeSelect = (userSelectedTime: number) => {
    if (!onTimeSelect) return;

    if (availableTimes) {
      setSelectedTime(availableTimes.times[userSelectedTime]);
      onTimeSelect(availableTimes.times[userSelectedTime]);
    }
  };

  const formatWeekdayName = (date: Date): React.ReactNode => (
    <span>{t(dayjs(date).format('ddd'))}</span>
  );

  const formatSelectedDay = (): React.ReactNode => {
    const tmp = dayjs(selectedDay)
      .locale(localeLanguage || 'en')
      .format('dddd, MMMM Do, YYYY');
    return tmp;
  };

  if (!fromDate || !toDate) return null;

  return (
    <div>
      <MediumHeader as="h2">{scheduleDeliveryFormHeader}</MediumHeader>
      <StyledCalendarContainer>
        <div style={{ ...bodyText }}>
          <StyledDayPicker
            mode="single"
            onSelect={setSelectedDay}
            fromDate={fromDate}
            toDate={toDate}
            disabled={disabledDates}
            defaultMonth={fromDate}
            selected={selectedDay}
            components={{
              CaptionLabel,
            }}
            formatters={{
              formatWeekdayName,
            }}
            retailerTheme={{
              daySelectedColor: iconsPrimary?.color,
            }}
          />
        </div>
        <div>
          {showNoDateSelected && (
            <StyledNoTimeSelectedContainer>
              <span style={{ ...bodyText, margin: '0 0 1em' }}>
                {scheduleDeliveryNoDateSelected}
              </span>
            </StyledNoTimeSelectedContainer>
          )}
          {showTimes && (
            <>
              <MediumHeader as="h3">
                {selectedDay && formatSelectedDay()}
              </MediumHeader>
              <br />
              <TimeSelector list={times} onSelect={handleTimeSelect} />
            </>
          )}
          {showNoTimes && (
            <>
              <MediumHeader as="h3">
                {selectedDay && formatSelectedDay()}
                <br />
                {selectedTime && (
                  <>
                    <br />
                    {selectedTime}
                    <br />
                  </>
                )}
              </MediumHeader>
              <p style={{ ...bodyText }}>
                {scheduleDeliveryInstructionsContentForCarrier}
              </p>
            </>
          )}
        </div>
      </StyledCalendarContainer>
      <Divider />
    </div>
  );
};

export default withThemeProvider(CalendarPanel, {
  themeTokens: ['bodytextDefaultNone', 'iconsPrimaryNone'],
});
